import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { MilestoneId, OnboardingProgress, Milestone, FeatureUnlock } from '@/types/onboarding';
import { onboardingService } from '@/services/onboardingService';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { mastermindSpacesService } from '@/services/mastermindSpacesService';

interface OnboardingContextType {
  progress: OnboardingProgress | null;
  isNewUser: boolean;
  isLoading: boolean;
  completeMilestone: (milestoneId: MilestoneId) => Promise<{ isNew: boolean; milestone: Milestone | undefined }>;
  hasMilestone: (milestoneId: MilestoneId) => boolean;
  isFeatureUnlocked: (feature: 'dashboard' | 'library' | 'tools' | 'advanced') => boolean;
  getUnlockedFeatures: () => FeatureUnlock[];
  getNextMilestone: () => Milestone | undefined;
  getCompletionPercentage: () => number;
  getTotalXpEarned: () => number;
  markWelcomeSeen: () => void;
  markTourSeen: (tour: 'chat' | 'library' | 'tools') => void;
  pendingCelebration: { milestone: Milestone; isNew: boolean } | null;
  clearCelebration: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [progress, setProgress] = useState<OnboardingProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingCelebration, setPendingCelebration] = useState<{ milestone: Milestone; isNew: boolean } | null>(null);

  // Check if user has spaces to auto-complete first_space milestone if needed
  const { data: spaces } = useQuery({
    queryKey: ['onboarding-spaces-check', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      return await mastermindSpacesService.getMastermindSpaces(null);
    },
    enabled: !!user?.id,
    staleTime: 60000, // Cache for 1 minute
  });

  useEffect(() => {
    if (user?.id) {
      // Handle test account reset first (clears localStorage for groundhog day mode)
      onboardingService.handleTestAccountLogin(user.id, user.email);
      // Pass user creation date and email to determine user type
      const userCreatedAt = user.createdAt ? new Date(user.createdAt) : undefined;
      const userProgress = onboardingService.getProgress(user.id, userCreatedAt, user.email);
      setProgress(userProgress);
      setIsLoading(false);
    } else {
      setProgress(null);
      setIsLoading(false);
    }
  }, [user?.id, user?.email, user?.createdAt]);

  // Auto-complete first_space milestone if user has spaces but milestone isn't completed
  useEffect(() => {
    if (user?.id && spaces && spaces.length > 0 && progress) {
      const hasFirstSpaceMilestone = onboardingService.hasMilestone(user.id, 'first_space');
      if (!hasFirstSpaceMilestone) {
        console.log('[Onboarding] Auto-completing first_space milestone - user has spaces');
        const result = onboardingService.completeMilestone(user.id, 'first_space');
        setProgress(result.progress);
      }
    }
  }, [user?.id, spaces, progress]);

  const completeMilestone = useCallback(async (milestoneId: MilestoneId) => {
    if (!user?.id) return { isNew: false, milestone: undefined };
    
    const result = onboardingService.completeMilestone(user.id, milestoneId);
    if (result.isNew && result.milestone) {
      setProgress(result.progress);
      setPendingCelebration({ milestone: result.milestone, isNew: true });
    }
    return result;
  }, [user?.id]);

  const hasMilestone = useCallback((milestoneId: MilestoneId) => {
    if (!user?.id) return false;
    return onboardingService.hasMilestone(user.id, milestoneId);
  }, [user?.id]);

  const isFeatureUnlocked = useCallback((feature: 'dashboard' | 'library' | 'tools' | 'advanced') => {
    if (!user?.id) return false;
    return onboardingService.isFeatureUnlocked(user.id, feature);
  }, [user?.id]);

  const getUnlockedFeatures = useCallback(() => {
    if (!user?.id) return [];
    return onboardingService.getUnlockedFeatures(user.id);
  }, [user?.id]);

  const getNextMilestone = useCallback(() => {
    if (!user?.id) return undefined;
    return onboardingService.getNextMilestone(user.id);
  }, [user?.id]);

  const getCompletionPercentage = useCallback(() => {
    if (!user?.id) return 0;
    return onboardingService.getCompletionPercentage(user.id);
  }, [user?.id]);

  const getTotalXpEarned = useCallback(() => {
    if (!user?.id) return 0;
    return onboardingService.getTotalXpEarned(user.id);
  }, [user?.id]);

  const markWelcomeSeen = useCallback(() => {
    if (!user?.id) return;
    onboardingService.markWelcomeSeen(user.id);
    setProgress(onboardingService.getProgress(user.id));
  }, [user?.id]);

  const markTourSeen = useCallback((tour: 'chat' | 'library' | 'tools') => {
    if (!user?.id) return;
    onboardingService.markTourSeen(user.id, tour);
    setProgress(onboardingService.getProgress(user.id));
  }, [user?.id]);

  const clearCelebration = useCallback(() => {
    setPendingCelebration(null);
  }, []);

  const isNewUser = progress ? progress.completedMilestones.length === 0 : true;

  return (
    <OnboardingContext.Provider
      value={{
        progress,
        isNewUser,
        isLoading,
        completeMilestone,
        hasMilestone,
        isFeatureUnlocked,
        getUnlockedFeatures,
        getNextMilestone,
        getCompletionPercentage,
        getTotalXpEarned,
        markWelcomeSeen,
        markTourSeen,
        pendingCelebration,
        clearCelebration,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}
